import React, { useState } from 'react';
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Edit, MoreHorizontal, User, Shield, Percent } from "lucide-react";
import { UserProfile } from "@/hooks/useUsersAdmin";
import { EditCommissionDialog } from "./EditCommissionDialog";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface UserTableRowProps {
  user: UserProfile;
  onEdit: (user: UserProfile) => void;
  onChangeRole: (userId: string, newRole: string) => void;
  onCommissionUpdated?: () => void;
}

export const UserTableRow: React.FC<UserTableRowProps> = ({ 
  user, 
  onEdit, 
  onChangeRole, 
  onCommissionUpdated 
}) => {
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  
  const formattedDate = user.created_at 
    ? format(new Date(user.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
    : "Desconhecido";

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return "bg-red-500/20 text-red-400 border-red-500/30";
      case 'mentor':
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      default:
        return "bg-green-500/20 text-green-400 border-green-500/30";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return "Administrador";
      case 'mentor':
        return "Mentor";
      default:
        return "Jogador";
    }
  };

  const formatCommissionRate = (rate?: number) => {
    if (!rate) return "20% (Padrão)";
    const percentage = (rate * 100).toFixed(1);
    return percentage === "20.0" ? "20% (Padrão)" : `${percentage}% (Personalizado)`;
  };

  return (
    <>
      <TableRow className="border-b border-[#222A35]">
        <TableCell className="py-3">
          <div className="font-medium text-white">{user.nickname}</div>
          <div className="text-sm text-gray-400 mt-1">{user.email}</div>
        </TableCell>
        <TableCell className="text-white">{user.preferred_game || "-"}</TableCell>
        <TableCell>
          <div className="flex flex-col gap-1">
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeClass(user.role)}`}>
              {getRoleLabel(user.role)}
            </div>
            {user.role === 'mentor' && (
              <Badge variant="outline" className="text-xs text-orange-400 border-orange-400/30 w-fit">
                {formatCommissionRate(user.commission_rate)}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-gray-400">{formattedDate}</TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                <span className="sr-only">Abrir menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#181D24] border-[#222A35] text-white">
              <DropdownMenuLabel>Ações</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#222A35]" />
              <DropdownMenuItem 
                className="hover:bg-[#222A35] cursor-pointer"
                onClick={() => onEdit(user)}
              >
                <Edit className="mr-2 h-4 w-4" />
                <span>Editar usuário</span>
              </DropdownMenuItem>
              
              {user.role === 'mentor' && (
                <>
                  <DropdownMenuSeparator className="bg-[#222A35]" />
                  <DropdownMenuItem 
                    className="hover:bg-[#222A35] cursor-pointer"
                    onClick={() => setShowCommissionDialog(true)}
                  >
                    <Percent className="mr-2 h-4 w-4" />
                    <span>Editar comissão</span>
                  </DropdownMenuItem>
                </>
              )}
              
              <DropdownMenuSeparator className="bg-[#222A35]" />
              <DropdownMenuLabel>Alterar função</DropdownMenuLabel>
              {user.role !== 'admin' && (
                <DropdownMenuItem 
                  className="hover:bg-[#222A35] cursor-pointer"
                  onClick={() => onChangeRole(user.id, 'admin')}
                >
                  <Shield className="mr-2 h-4 w-4" />
                  <span>Tornar administrador</span>
                </DropdownMenuItem>
              )}
              {user.role !== 'mentor' && (
                <DropdownMenuItem 
                  className="hover:bg-[#222A35] cursor-pointer"
                  onClick={() => onChangeRole(user.id, 'mentor')}
                >
                  <User className="mr-2 h-4 w-4" />
                  <span>Tornar mentor</span>
                </DropdownMenuItem>
              )}
              {user.role !== 'jogador' && (
                <DropdownMenuItem 
                  className="hover:bg-[#222A35] cursor-pointer"
                  onClick={() => onChangeRole(user.id, 'jogador')}
                >
                  <User className="mr-2 h-4 w-4" />
                  <span>Tornar jogador</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>

      {user.role === 'mentor' && (
        <EditCommissionDialog
          isOpen={showCommissionDialog}
          onClose={() => setShowCommissionDialog(false)}
          mentorId={user.id}
          mentorName={user.nickname}
          currentRate={(user.commission_rate || 0.2) * 100}
          onSuccess={() => {
            onCommissionUpdated?.();
          }}
        />
      )}
    </>
  );
};
